import type {
	AuthorizeResponse,
	CodeResponse,
	DevLoginResponse,
	OAuthParams,
	OtpSendResponse,
	OtpVerifyResponse,
	SessionResponse,
} from './types'

interface ApiOptions {
	method?: string
	body?: unknown
	headers?: Record<string, string>
}

/** A failed API call, with the HTTP status so callers can tell causes apart. */
export class ApiError extends Error {
	public constructor(
		message: string,
		public readonly status: number,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

async function request<T>(baseUrl: string, path: string, options: ApiOptions = {}): Promise<T> {
	const { method = 'GET', body, headers = {} } = options

	const res = await fetch(`${baseUrl}${path}`, {
		method,
		headers: {
			'Content-Type': 'application/json',
			...headers,
		},
		credentials: 'include',
		body: body ? JSON.stringify(body) : undefined,
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: 'Request failed' }))
		throw new ApiError((error as { message?: string }).message ?? `HTTP ${res.status}`, res.status)
	}

	return res.json() as Promise<T>
}

export function createOAuthClient(baseUrl: string) {
	const api = <T>(path: string, options?: ApiOptions) => request<T>(baseUrl, path, options)

	return {
		sendOtp(identifier: string): Promise<OtpSendResponse> {
			return api<OtpSendResponse>('/auth/otp/send', {
				method: 'POST',
				body: { identifier: identifier.trim(), type: 'email' },
			})
		},

		verifyOtp(identifier: string, code: string): Promise<OtpVerifyResponse> {
			return api<OtpVerifyResponse>('/auth/otp/verify', {
				method: 'POST',
				body: { identifier: identifier.trim(), code, type: 'email' },
			})
		},

		devLogin(identifier?: string): Promise<DevLoginResponse> {
			return api<DevLoginResponse>('/auth/dev-login', {
				method: 'POST',
				body: { identifier: identifier?.trim() ?? '' },
			})
		},

		// The session is for whoever the access token names: the account the
		// sign-in just returned it for.
		createSession(accessToken: string): Promise<SessionResponse> {
			return api<SessionResponse>('/oauth/session', {
				method: 'POST',
				headers: { Authorization: `Bearer ${accessToken}` },
			})
		},

		getAuthorize(params: OAuthParams): Promise<AuthorizeResponse> {
			const query = new URLSearchParams({
				client_id: params.clientId,
				redirect_uri: params.redirectUri,
				response_type: params.responseType ?? 'code',
				state: params.state,
				code_challenge: params.codeChallenge,
				code_challenge_method: params.codeChallengeMethod,
				...(params.scope ? { scope: params.scope } : {}),
			})
			return api<AuthorizeResponse>(`/oauth/authorize?${query.toString()}`)
		},

		// For the account of the session cookie createSession set.
		postAuthorize(params: OAuthParams): Promise<CodeResponse> {
			return api<CodeResponse>('/oauth/authorize', {
				method: 'POST',
				body: {
					clientId: params.clientId,
					redirectUri: params.redirectUri,
					codeChallenge: params.codeChallenge,
					codeChallengeMethod: params.codeChallengeMethod,
					state: params.state,
					scope: params.scope,
				},
			})
		},
	}
}
